import type { CollectionProps } from '@react-aria/collections';
import clsx from 'clsx';
import type React from 'react';
import {
    Collection,
    Header,
    ListBox,
    ListBoxItem,
    type ListBoxProps,
    ListBoxSection,
} from 'react-aria-components';

export interface Section<Item extends object> {
    key: string;
    items: Item[];
}

export interface SectionRenderItemInfo<Item extends object> {
    item: Item;
    itemKey: string;
    sectionKey: string;
}

export interface SectionRenderSectionHeaderInfo<Item extends object> {
    section: Section<Item>;
}

export type SectionsListSelectionProps<Item extends object> = Pick<
    ListBoxProps<Section<Item>>,
    'selectedKeys' | 'selectionMode' | 'selectionBehavior' | 'onSelectionChange' | 'className'
>;

export interface SectionsListProps<Item extends object>
    extends SectionsListSelectionProps<Item>,
        Pick<CollectionProps<Item>, 'dependencies'> {
    /** The list of items grouped by sections */
    items: Section<Item>[];

    /** Get the key for an item. */
    getSectionItemKey: (item: Item) => string;

    /** Render a single item from the sectioned list. */
    renderSectionItem: (info: SectionRenderItemInfo<Item>) => React.ReactElement;

    /** Render the header a section. */
    renderSectionHeader: (info: SectionRenderSectionHeaderInfo<Item>) => React.ReactElement;
}

export function SectionsList<Item extends object>(props: SectionsListProps<Item>) {
    const { items, renderSectionItem, renderSectionHeader, dependencies, ...rest } = props;

    return (
        <ListBox {...rest} dependencies={dependencies} items={items}>
            {(section) => (
                <ListBoxSection key={section.key} id={section.key}>
                    <Header>{renderSectionHeader({ section })}</Header>
                    <Collection dependencies={dependencies} items={section.items}>
                        {(item) => {
                            const itemKey = props.getSectionItemKey(item);
                            return (
                                <ListBoxItem
                                    key={itemKey}
                                    id={itemKey}
                                    className={clsx(
                                        props.onSelectionChange &&
                                            'cursor-pointer hover:bg-list-item-hover focus-visible:shadow-focus-ring'
                                    )}
                                >
                                    {renderSectionItem({
                                        item,
                                        sectionKey: section.key,
                                        itemKey,
                                    })}
                                </ListBoxItem>
                            );
                        }}
                    </Collection>
                </ListBoxSection>
            )}
        </ListBox>
    );
}
